/**
 * Autoritativer Auth-Check, entkoppelt vom Sliding-Session-Heartbeat.
 *
 * Nach TireVault-Vorbild (apps/tirevault/frontend/src/hooks/useAuthQueries.ts,
 * siehe Memory project_rentalhub_superadmin_portal_migration fuer den Kontext
 * der Session-System-Angleichung): der Heartbeat unten ist idle-gegatet und
 * verlaengert nur die Sliding-Session bei echter Nutzung — die eigentliche
 * "bin ich noch eingeloggt"-Pruefung (useAuthUser selbst) laeuft davon
 * unabhaengig immer bei Mount und bei Tab-Fokus (refetchOnWindowFocus), egal
 * ob der Nutzer gerade als idle gilt. Vorher lief der einzige Fokus-Recheck
 * innerhalb des idle-gegateten Heartbeats in App.tsx — ein nach langer
 * Inaktivitaet abgelaufener Login wurde dadurch bei Tab-Rueckkehr nicht
 * erkannt (identischer Bug wie project_tirevault_session_logout_bug).
 */
import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import type { User } from '../types';

const IDLE_THRESHOLD_MS = 5 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;

export const AUTH_USER_QUERY_KEY = ['auth-user'] as const;

let lastActivityAt = Date.now();
let activityTrackingStarted = false;

function ensureActivityTracking() {
  if (activityTrackingStarted || typeof window === 'undefined') return;
  activityTrackingStarted = true;
  const markActive = () => { lastActivityAt = Date.now(); };
  ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
}

async function fetchAuthUser(): Promise<User> {
  const { data } = await api.get<User>('/auth/me');
  return data;
}

function useSessionHeartbeat() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityAt > IDLE_THRESHOLD_MS) return;
      queryClient
        .fetchQuery({ queryKey: AUTH_USER_QUERY_KEY, queryFn: fetchAuthUser, staleTime: 0 })
        .catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [queryClient]);
}

export function useAuthUser() {
  ensureActivityTracking();
  useSessionHeartbeat();

  return useQuery<User>({
    queryKey: AUTH_USER_QUERY_KEY,
    queryFn: fetchAuthUser,
    retry: false,
    staleTime: 0,                 // bei jeder SPA-Navigation Auth neu pruefen
    gcTime: 10 * 60 * 1000,       // gecachte Daten behalten, um Spinner bei Tab-Wechsel zu vermeiden
    refetchOnWindowFocus: true,   // echter Server-Check bei Tab-Rueckkehr, unabhaengig vom Idle-Status
  });
}
